import type { PageEntry, Entry, Node } from 'contensis-delivery-api';
import htmlParser from 'html-react-parser';
import { format } from 'date-fns';
import { enGB } from 'date-fns/locale';
import { Tick } from '@solent-university/solent-icons';

import { useTheme } from 'styled-components';

import cmsParser from 'Common/util/cmsParser';
import { composerOpenDays, composerHeroBanner } from 'Common/util/cmsComposer';

import ArrowLinkButton from 'Elements/Button/Arrow';

import Grid, { Col } from 'Components/Grid';
import {
    EventAccordionHeading,
    EventAccordionParagraph
} from 'Components/Accordion/Event/Event.styled';

import type { Props } from './Events';

export type PageData = {
    entry: PageEntry;
    events: Entry[];
    ancestors: Node[];
};

const datamap = (data: PageData): Props => {
    const { entry, events, ancestors } = data;

    const {
        title,
        sys,
        childContentType,

        // Hero banner
        heroBanner,
        bannerDescription,

        // Voucher
        promoPointVoucherValue,
        promoPointHeading,
        promoPointDescription,
        promoPointLink,

        // Campus tour
        otherEventsHeading,
        otherEventsDescription,
        otherEventsLink,

        // Tef award
        tefAward,

        // Image fade banner
        imageFadeBanner,

        // On Demand
        onDemandHeading,
        onDemandBenefits,
        onDemandOverviewHeading,
        onDemandOverviewText,
        onDemandLink,

        // Upcoming Events
        relatedEventsHeading,
        relatedEvents,

        // Contact Us
        contactHeading,
        secondContactEmail,
        socialMediaLink
    } = entry;

    const { components } = useTheme();

    const heroHeadingColour = components.events[childContentType]?.text
        ? components.events[childContentType].text
        : heroBanner[0].value.heroBannerWaveDefault.isClearingVariant
        ? 'white'
        : components.events.default.text;

    return {
        breadcrumbs: cmsParser.breadcrumbs([...ancestors, { entry: { title: title }, id: sys.id }]),
        heroBanner: composerHeroBanner.ImageVideo(heroBanner[0], {
            headingColour: heroHeadingColour
        }),
        eventIntroText: htmlParser(bannerDescription),
        nextEvent:
            events.length !== 0
                ? {
                      text: `Our next open day is on:`,
                      date: format(new Date(events[0].date.from), 'dd MMMM yyyy', {
                          locale: enGB
                      }),
                      link: {
                          label: 'Book now',
                          ariaLabel: 'Click to book now',
                          href: events[0].bookingLink.entryTitle,
                          openInNewTab: true
                      }
                  }
                : undefined,
        voucher: {
            heading: promoPointVoucherValue,
            subheading: promoPointHeading,
            information: promoPointDescription,
            link: { ...cmsParser.link(promoPointLink), variant: 'sixDark' }
        },
        eventAccordions: {
            items: events.map(event => {
                return {
                    id: event.sys.id,
                    button: {
                        ariaLabel: `View ${format(new Date(event.date.from), 'iiii dd MMMM yyyy', {
                            locale: enGB
                        })}`
                    },
                    heading: format(new Date(event.date.from), 'iiii dd MMMM yyyy', {
                        locale: enGB
                    }),
                    headingType: 'h2',
                    children: (
                        <Grid style={{ paddingTop: '2rem' }}>
                            <Col m={6}>
                                <ArrowLinkButton
                                    {...{
                                        label: 'Book',
                                        href: event.bookingLink.entryTitle,
                                        ariaLabel: `Book ${event.entryTitle} now`,
                                        openInNewTab: true
                                    }}
                                />
                                <EventAccordionHeading>Where</EventAccordionHeading>
                                <EventAccordionParagraph>{event.location}</EventAccordionParagraph>
                                {event.leftColumnContent &&
                                    composerOpenDays.accordion(event.leftColumnContent)}
                            </Col>
                            <Col m={6}>
                                {event.rightColumnContent &&
                                    composerOpenDays.accordion(event.rightColumnContent)}
                            </Col>
                        </Grid>
                    ),
                    type: event.type,
                    location: event.delivery
                };
            })
        },
        tefAward: {
            heading: tefAward.promoPointHeading,
            text: tefAward.promoPointDescription,
            link: tefAward.promoPointButton
                ? cmsParser.linkButton(tefAward.promoPointButton)
                : undefined,
            image: cmsParser.image(tefAward.tefImage)
        },
        imageFadeBanner: {
            id: 'image-fade-banner',
            heading: imageFadeBanner.heading,
            description: imageFadeBanner.description,
            image: cmsParser.image(imageFadeBanner.image),
            link: imageFadeBanner.link.link
                ? cmsParser.linkButton(imageFadeBanner.link)
                : undefined,
            imageRight: imageFadeBanner.imageDisplaysRight
                ? imageFadeBanner.imageDisplaysRight
                : undefined
        },
        campusTour: {
            id: 'campus-tour',
            heading: otherEventsHeading,
            description: otherEventsDescription,
            link: cmsParser.linkButton(otherEventsLink)
        },
        onDemand: {
            id: 'on-demand',
            heading: onDemandHeading,
            overviewTitle: onDemandOverviewHeading,
            overviewText: htmlParser(onDemandOverviewText),
            button: cmsParser.linkButton(onDemandLink),
            whatYouGet: {
                heading: onDemandBenefits.label,
                items: onDemandBenefits.item.map(benefit => {
                    return { label: benefit, icon: Tick };
                })
            }
        },
        upcomingEvents: {
            heading: relatedEventsHeading,
            events: relatedEvents.map(event => {
                return {
                    id: event.sys.id,
                    title: event.entryTitle,
                    date: new Date(event.eventDate.from),
                    link: {
                        label: event.callToAction.label,
                        ariaLabel: `Click to register for ${event.entryTitle}`,
                        href: event.callToAction.linkPath,
                        openInNewTab: true
                    }
                };
            }),
            noEvents: {
                content: ''
            },
            dateOptions: {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
            } as Intl.DateTimeFormatOptions
        },
        contactUs: {
            id: 'contact-us',
            heading: contactHeading,
            generalQuestions: {
                link: {
                    label: secondContactEmail.email,
                    ariaLabel: 'Click to contact Solent',
                    href: `mailto:${secondContactEmail.email}`,
                    openInNewTab: true
                }
            },
            useWave: false,
            socials:
                socialMediaLink.length > 0
                    ? Object.fromEntries(
                          socialMediaLink.map(item => [item.socialMediaType, item.link])
                      )
                    : undefined
        }
    };
};

export default datamap;
